/* eslint-disable global-require, no-underscore-dangle, no-param-reassign, no-void, no-shadow */
/* eslint-disable func-names */
import uuid from 'uuid';
import eventing from '../../helpers/eventing';
import arrayRemoveItem from '../../helpers/arrayRemoveItem';
import { collectRtcStatsReportHelper, collectGetStatsHelper } from './collectStatsHelper';

export default function SinglePeerConnectionAdapterFactory() {
  return function SinglePeerConnectionAdapter(options, controller) {
    const _transceivers = {};
    const _tracks = [];
    let _iceConnectedEmitted = false;

    this._id = uuid();

    // SPC events wrapper for PeerConnection

    const _onIceConnected = () => {
      this.trigger('iceConnected');
      _iceConnectedEmitted = true;
    };
    const _onClosed = () => this.trigger('close');
    const _onRemoteVideoSupported = supported => this.trigger('remoteVideoSupported', supported);
    const _onPeerError = ({ reason, prefix }) => this.trigger('error', reason, prefix);
    const _onQoS = (qos) => {
      qos.parsedStats.isSinglePeerConnection = true;
      this.trigger('qos', qos);
    };
    const _onIceConnectionStateChange = state => this.trigger('iceConnectionStateChange', state);
    const _onSignalingStateChange = state => this.trigger('signalingStateChange', state);
    const _onSignalingStateStable = state => this.trigger('signalingStateStable', state);

    eventing(this);

    const _peerConnection = controller.getPeerConnection(options, this);

    _peerConnection.on({
      iceConnected: _onIceConnected,
      close: _onClosed,
      signalingStateChange: _onSignalingStateChange,
      signalingStateStable: _onSignalingStateStable,
      error: _onPeerError,
      qos: _onQoS,
      iceConnectionStateChange: _onIceConnectionStateChange,
      remoteVideoSupported: _onRemoteVideoSupported,
    }, this);

    // Specific SPC logic

    const shouldEmitIceConnected = () => _peerConnection.iceConnectionStateIsConnected() &&
     !_iceConnectedEmitted;

    this._onTrackAdded = (remoteRTCStream) => {
      const { track, transceiver } = remoteRTCStream;
      _transceivers[track.id] = transceiver.mid;
      controller.addSubscriberMid(transceiver.mid, this._id);
      _tracks.push(track);
      if (shouldEmitIceConnected()) {
        // Since PC is already connected, we will need to emit the event now.
        _onIceConnected();
      }
      this.trigger('trackAdded', remoteRTCStream);
    };

    this.disconnect = () => {
      controller.removeSubscriber(this._id, Object.values(_transceivers));
    };

    this.removeTrack = (trackToRemove) => {
      if (_transceivers[trackToRemove.id]) {
        controller.removeSubscriber(this._id, [_transceivers[trackToRemove.id]]);
      }

      arrayRemoveItem(_tracks, trackToRemove);
      controller.removeTrack(trackToRemove, this._id);
    };

    this.remoteTracks = () => _tracks;

    // SPC wrapper for PeerConnection

    this.generateOfferAndSend = () => _peerConnection.generateOfferAndSend();

    this.getDataChannel = (label, options, completion) => {
      _peerConnection.getDataChannel(label, options, completion);
    };

    this.getSourceStreamId = () => _peerConnection.getSourceStreamId();

    this.processOffer = (message) => {
      message.content.sdp = controller.processOffer(message.content.sdp);
      _peerConnection.processMessage('offer', message);
    };

    this.processAnswer = (message) => {
      message.content.sdp = controller.processAnswer(message.content.sdp);

      _peerConnection.processMessage('answer', message);
      // When Mantis sent an answer, the negotiation is done.
      controller.subscriberComplete();
    };

    this.processMessage = (type, message) => {
      if (type === 'offer') {
        this.processOffer(message);
      } else if (type === 'answer') {
        this.processAnswer(message);
      } else {
        _peerConnection.processMessage(type, message);
      }
    };

    this.remoteDescription = () => _peerConnection.remoteDescription();

    this.getStats = (track, callback) => {
      if (track) {
        // getStats for a specific track.
        _peerConnection.getStats(track, callback);
      } else {
        // getStats for all tracks.
        collectGetStatsHelper(_peerConnection, _tracks, callback);
      }
    };

    this.getSynchronizationSources = (callback) => {
      _peerConnection.getSynchronizationSources(callback);
    };

    this.getRtcStatsReport = (track, callback) => {
      if (track) {
        // getRtcStatsReport for a specific track.
        _peerConnection.getRtcStatsReport(track, callback);
      } else {
        // getRtcStatsReport for all tracks.
        collectRtcStatsReportHelper(_peerConnection, _tracks, callback);
      }
    };

    this.remoteStreams = () => _peerConnection.remoteStreams();

    this.hasRelayCandidates = () => _peerConnection.hasRelayCandidates();

    this.iceRestart = () => _peerConnection.iceRestart();

    this.iceConnectionStateIsConnected = () => _peerConnection.iceConnectionStateIsConnected();
  };
}
